import { Notification } from "providers/NotifierProvider";
import { supabase } from "supabase/client";
import logSentryError from "utils/logSentryError";

export enum StorageBuckets {
  PROFILES_AVATARS = "profiles-avatars",
  GROUPS_AVATARS = "groups-avatars",
}

class StorageApi {
  showNotification?: (notification: Notification) => void;

  constructor(showNotification?: (notification: Notification) => void) {
    this.showNotification = showNotification;
  }

  /**
   * Save image to storage
   *
   * @param image
   * @param fileName
   * @param folderPath
   * @returns
   */
  async saveImage(image: Blob, fileName: string, folderPath: string): Promise<{ error: Error | null }> {
    const { error } = await supabase.storage.from(folderPath).upload(fileName, image);

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to save profile image.",
          severity: "error",
        });

        logSentryError(error, "STORAGE_API: saveImage");
        return { error };
      }
    }

    return { error: null };
  }

  /**
   * Get image from storage
   *
   * @param fileName
   * @param folderPath
   * @returns
   */
  async getFile(fileName: string, folderPath: string): Promise<{ file: Blob | null; error: Error | null }> {
    const { data, error } = await supabase.storage.from(folderPath).download(fileName);

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to load file.",
          severity: "error",
        });

        logSentryError(error, "STORAGE_API: getFile");
        return { file: null, error };
      }
    }

    return { file: data, error: null };
  }

  /**
   * Remove file from storage
   *
   * @param fileNames
   * @param fileNames
   * @returns
   */
  async deleteFiles(bucket: string, fileNames: Array<string>): Promise<{ error: Error | null }> {
    const { error } = await supabase.storage.from(bucket).remove(fileNames);

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to delete file.",
          severity: "error",
        });

        logSentryError(error, "STORAGE_API: deleteFiles");
        return { error };
      }
    }

    return { error: null };
  }
}

export default StorageApi;
